import { ObjectSchema, TestConfig, boolean, mixed, number, object } from "yup";

import { CompletionStatusType } from "@/components/refineRealEstate/dataGroupsConstants";
import {
  BuildingStandardDeterminationMethodEnum,
  BuildingTypeEnum,
  Construction,
  EnergyCertificateTypeEnum,
  ModernizationMethodEnum,
  ObjectTypeEnum,
  OwnershipEnum,
  ProductTypeEnum,
} from "@/types/refineRealEstate";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";

import {
  REAL_ESTATE_VALIDATION_LIMITS,
  getGarageLimits,
  getLivingAreaLimits,
  getUnitsNumberLimits,
} from "./realEstateValidationLimits";

const createRangeValidation = (min: number, max: number): TestConfig<number | undefined> => ({
  name: "range",
  message: `VALUE_OUT_OF_RANGE|${min}|${max}`,
  params: { min, max },
  test: (value: number | undefined) => value === undefined || (value >= min && value <= max),
});

const nullishToNumber = (originalValue: number | string | null): number | undefined => {
  if (originalValue === "" || originalValue === null) {
    return undefined;
  }
  return isNaN(Number(originalValue)) ? undefined : Number(originalValue);
};

const validationLimits = REAL_ESTATE_VALIDATION_LIMITS;
const apartmentLivingAreaLimits = getLivingAreaLimits(ObjectTypeEnum.apartment);
const multiFamilyLivingAreaLimits = getLivingAreaLimits(ObjectTypeEnum.multiFamilyHouse);
const defaultLivingAreaLimits = getLivingAreaLimits();
const apartmentUnitsNumberLimits = getUnitsNumberLimits(ObjectTypeEnum.apartment)!;
const multiFamilyUnitsNumberLimits = getUnitsNumberLimits(ObjectTypeEnum.multiFamilyHouse)!;

export const realEstateRefinementSchema: ObjectSchema<PropertyInformation> = object().shape({
  generalInfo: object().shape({
    objectType: mixed<ObjectTypeEnum>().required("REQUIRED_FIELD").oneOf(Object.values(ObjectTypeEnum)),
    buildingType: mixed<BuildingTypeEnum>().optional(),
    productType: mixed<ProductTypeEnum>().required("REQUIRED_FIELD").oneOf(Object.values(ProductTypeEnum)),
    ownership: mixed<OwnershipEnum>().required("REQUIRED_FIELD").oneOf(Object.values(OwnershipEnum)),
    remainingDuration: number()
      .transform(nullishToNumber)
      .typeError("MUST_BE_NUMBER")
      .optional()
      .test({
        name: "min-value",
        message: ({ min }) => `VALUE_MUST_BE_GREATER_THAN|${min}`,
        params: { min: validationLimits.remainingDuration.minExclusive },
        test: (value) => value === undefined || value > validationLimits.remainingDuration.minExclusive,
      }),
    actualGroundRent: number()
      .transform(nullishToNumber)
      .typeError("MUST_BE_NUMBER")
      .optional()
      .test({
        name: "min-value",
        message: ({ min }) => `VALUE_MUST_BE_GREATER_THAN_OR_EQUAL|${min}`,
        params: { min: validationLimits.actualGroundRent.min },
        test: (value) => value === undefined || value >= validationLimits.actualGroundRent.min,
      }),
    nonCompensableBuildingPortion: number()
      .transform(nullishToNumber)
      .typeError("MUST_BE_NUMBER")
      .optional()
      .test(
        createRangeValidation(
          validationLimits.nonCompensableBuildingPortion.min,
          validationLimits.nonCompensableBuildingPortion.max,
        ),
      ),
    completionStatus: mixed<CompletionStatusType>().required("REQUIRED_FIELD"),
  }),
  plotData: object().shape({
    plotArea: number()
      .typeError("MUST_BE_NUMBER")
      .when("$objectType", {
        is: (objectType: ObjectTypeEnum) =>
          objectType === ObjectTypeEnum.house ||
          objectType === ObjectTypeEnum.multiFamilyHouse ||
          objectType === ObjectTypeEnum.mixedUseProperty ||
          objectType === ObjectTypeEnum.plotOfLand,
        then: (schema) =>
          schema
            .required("REQUIRED_FIELD")
            .test(createRangeValidation(validationLimits.plotArea.min, validationLimits.plotArea.max)),
        otherwise: (schema) =>
          schema.optional().test(createRangeValidation(validationLimits.plotArea.min, validationLimits.plotArea.max)),
      }),
    coOwnershipShare: object().shape({
      numerator: number().transform(nullishToNumber).typeError("MUST_BE_NUMBER").optional(),
      denominator: number().transform(nullishToNumber).typeError("MUST_BE_NUMBER").optional(),
    }),
    completionStatus: mixed<CompletionStatusType>().required("REQUIRED_FIELD"),
  }),
  buildingData: object().shape(
    {
      constructionYear: number()
        .typeError("MUST_BE_NUMBER")
        .when("$objectType", {
          is: ObjectTypeEnum.plotOfLand,
          then: (schema) =>
            schema
              .optional()
              .test(createRangeValidation(validationLimits.constructionYear.min, validationLimits.constructionYear.max))
              .integer("MUST_BE_INTEGER"),
          otherwise: (schema) =>
            schema
              .required("REQUIRED_FIELD")
              .test(createRangeValidation(validationLimits.constructionYear.min, validationLimits.constructionYear.max))
              .integer("MUST_BE_INTEGER"),
        }),
      livingArea: number()
        .typeError("MUST_BE_NUMBER")
        .transform(nullishToNumber)
        .when("$objectType", {
          is: ObjectTypeEnum.apartment,
          then: (schema) =>
            schema.test(createRangeValidation(apartmentLivingAreaLimits.min, apartmentLivingAreaLimits.max)),
          otherwise: (schema) =>
            schema.when("$objectType", {
              is: (objectType: ObjectTypeEnum) =>
                objectType === ObjectTypeEnum.multiFamilyHouse || objectType === ObjectTypeEnum.mixedUseProperty,
              then: (mfhSchema) =>
                mfhSchema.test(createRangeValidation(multiFamilyLivingAreaLimits.min, multiFamilyLivingAreaLimits.max)),
              otherwise: (schema) =>
                schema.test(createRangeValidation(defaultLivingAreaLimits.min, defaultLivingAreaLimits.max)),
            }),
        })
        .test("mfh-living-area-per-unit", function (value) {
          if (
            (this.options.context?.objectType !== ObjectTypeEnum.multiFamilyHouse &&
              this.options.context?.objectType !== ObjectTypeEnum.mixedUseProperty) ||
            value === undefined
          ) {
            return true;
          }

          const { unitsNumber } = this.parent;
          if (unitsNumber === undefined || unitsNumber === null || value >= unitsNumber) {
            return true;
          }

          return this.createError({
            message: "VALUE_MUST_BE_GREATER_THAN_OR_EQUAL|" + unitsNumber,
          });
        })
        .test("exclusive-area", function (value) {
          if (this.options.context?.objectType === ObjectTypeEnum.plotOfLand) {
            return true;
          }

          const { grossFloorArea } = this.parent;
          if (value === undefined && grossFloorArea === undefined) {
            return this.createError({
              message: "REQUIRED_FIELD",
            });
          }
          return true;
        }),
      grossFloorArea: number()
        .typeError("MUST_BE_NUMBER")
        .test(createRangeValidation(validationLimits.grossFloorArea.min, validationLimits.grossFloorArea.max))
        .transform(nullishToNumber)
        .test("exclusive-area", function (value) {
          if (this.options.context?.objectType === ObjectTypeEnum.plotOfLand) {
            return true;
          }

          const { livingArea } = this.parent;
          if (value === undefined && livingArea === undefined) {
            return this.createError({
              message: `REQUIRED_FIELD`,
            });
          }
          return true;
        }),
      rooms: number()
        .transform(nullishToNumber)
        .typeError("MUST_BE_NUMBER")
        .optional()
        .test(createRangeValidation(validationLimits.rooms.min, validationLimits.rooms.max)),
      floorNumber: number()
        .typeError("MUST_BE_NUMBER")
        .integer("MUST_BE_INTEGER")
        .optional()
        .test(createRangeValidation(validationLimits.floorNumber.min, validationLimits.floorNumber.max)),
      construction: mixed<Construction>().when("$objectType", {
        is: ObjectTypeEnum.house,
        then: (schema) => schema.required("REQUIRED_FIELD").oneOf(Object.values(Construction)),
        otherwise: (schema) => schema.optional(),
      }),
      numberOfFloors: number()
        .transform(nullishToNumber)
        .typeError("MUST_BE_NUMBER")
        .integer("MUST_BE_INTEGER")
        .when("$objectType", {
          is: (objectType: ObjectTypeEnum) =>
            objectType === ObjectTypeEnum.house ||
            objectType === ObjectTypeEnum.multiFamilyHouse ||
            objectType === ObjectTypeEnum.mixedUseProperty,
          then: (schema) =>
            schema
              .required("REQUIRED_FIELD")
              .test(createRangeValidation(validationLimits.numberOfFloors.min, validationLimits.numberOfFloors.max)),
          otherwise: (schema) =>
            schema
              .optional()
              .test(createRangeValidation(validationLimits.numberOfFloors.min, validationLimits.numberOfFloors.max)),
        }),
      unitsNumber: number()
        .transform(nullishToNumber)
        .typeError("MUST_BE_NUMBER")
        .integer("MUST_BE_INTEGER")
        .when("$objectType", {
          is: ObjectTypeEnum.apartment,
          then: (schema) =>
            schema.required("REQUIRED_FIELD").test({
              name: "min-value",
              message: `VALUE_MUST_BE_GREATER_THAN_OR_EQUAL|${apartmentUnitsNumberLimits.min}`,
              test: (value) => value !== undefined && value >= apartmentUnitsNumberLimits.min,
            }),
          otherwise: (schema) =>
            schema.when("$objectType", {
              is: (objectType: ObjectTypeEnum) =>
                objectType === ObjectTypeEnum.multiFamilyHouse || objectType === ObjectTypeEnum.mixedUseProperty,
              then: (mfhSchema) =>
                mfhSchema
                  .required("REQUIRED_FIELD")
                  .test(createRangeValidation(multiFamilyUnitsNumberLimits.min, multiFamilyUnitsNumberLimits.max!)),
              otherwise: (schema) => schema.optional(),
            }),
        }),
      commercialArea: number()
        .transform(nullishToNumber)
        .typeError("MUST_BE_NUMBER")
        .when("$objectType", {
          is: ObjectTypeEnum.mixedUseProperty,
          then: (schema) =>
            schema.required("REQUIRED_FIELD").test({
              name: "min-value",
              message: "VALUE_MUST_BE_GREATER_THAN|0",
              test: (value) => value !== undefined && value > 0,
            }),
          otherwise: (schema) => schema.optional(),
        }),
      commercialUnits: number()
        .transform(nullishToNumber)
        .typeError("MUST_BE_NUMBER")
        .integer("MUST_BE_INTEGER")
        .when("$objectType", {
          is: ObjectTypeEnum.mixedUseProperty,
          then: (schema) => schema.required("REQUIRED_FIELD").test(createRangeValidation(1, 998)),
          otherwise: (schema) => schema.optional().test(createRangeValidation(1, 998)),
        }),
      garages: number()
        .transform(nullishToNumber)
        .typeError("MUST_BE_NUMBER")
        .integer("MUST_BE_INTEGER")
        .optional()
        .test(createRangeValidation(getGarageLimits().min, getGarageLimits().max)),
      outdoorParkingSpaces: number()
        .transform(nullishToNumber)
        .typeError("MUST_BE_NUMBER")
        .integer("MUST_BE_INTEGER")
        .optional()
        .test(
          createRangeValidation(validationLimits.outdoorParkingSpaces.min, validationLimits.outdoorParkingSpaces.max),
        ),
      completionStatus: mixed<CompletionStatusType>().required("REQUIRED_FIELD"),
    },
    [["livingArea", "grossFloorArea"]],
  ),
  energyAndPhotovoltaic: object().shape({
    certificateType: mixed<EnergyCertificateTypeEnum>()
      .nullable()
      .transform((value) => (value === "" ? undefined : value))
      .when("energyCertificate", {
        is: (energyCertificate: string | null | undefined) => Boolean(energyCertificate),
        then: (schema) => schema.required("REQUIRED_FIELD"),
        otherwise: (schema) => schema.optional(),
      }),
    finalEnergy: number()
      .transform(nullishToNumber)
      .typeError("MUST_BE_NUMBER")
      .optional()
      .test(createRangeValidation(validationLimits.energy.min, validationLimits.energy.max)),
    primaryEnergy: number()
      .transform(nullishToNumber)
      .typeError("MUST_BE_NUMBER")
      .optional()
      .test(createRangeValidation(validationLimits.energy.min, validationLimits.energy.max)),
    CO2Emissions: number()
      .transform(nullishToNumber)
      .typeError("MUST_BE_NUMBER")
      .optional()
      .test(createRangeValidation(validationLimits.energy.min, validationLimits.energy.max)),
    photovoltaicAvailable: boolean().nullable().optional(),
    constructionYear: number()
      .transform(nullishToNumber)
      .when("photovoltaicAvailable", {
        is: true,
        then: function (schema) {
          return schema
            .required("REQUIRED_FIELD")
            .integer("MUST_BE_INTEGER")
            .test({
              name: "photovoltaic-construction-year-range",
              test: function (value) {
                if (value === undefined || value === null) return false;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const constructionYear = (this as any).from?.[1]?.value?.buildingData?.constructionYear;
                const minYear =
                  constructionYear !== undefined &&
                  constructionYear !== null &&
                  constructionYear >= validationLimits.photovoltaicConstructionYear.min
                    ? constructionYear
                    : validationLimits.photovoltaicConstructionYear.min;
                if (value < minYear || value > validationLimits.photovoltaicConstructionYear.formMax) {
                  return this.createError({
                    message: `VALUE_OUT_OF_RANGE|${minYear}|${validationLimits.photovoltaicConstructionYear.formMax}`,
                    path: this.path,
                  });
                }
                return true;
              },
            });
        },
        otherwise: (schema) => schema.optional().transform((value) => (value === "" ? undefined : value)),
      }),
    annualPowerYield: number()
      .transform(nullishToNumber)
      .when("photovoltaicAvailable", {
        is: true,
        then: (schema) =>
          schema
            .required("REQUIRED_FIELD")
            .test(
              createRangeValidation(
                validationLimits.photovoltaicAnnualPowerYield.min,
                validationLimits.photovoltaicAnnualPowerYield.max,
              ),
            )
            .integer("MUST_BE_INTEGER"),
        otherwise: (schema) => schema.optional().transform((value) => (value === "" ? undefined : value)),
      }),
    completionStatus: mixed<CompletionStatusType>().required("REQUIRED_FIELD"),
  }),
  buildingStandard: object().shape({
    method: mixed<BuildingStandardDeterminationMethodEnum>(),
    completionStatus: mixed<CompletionStatusType>().required("REQUIRED_FIELD"),
  }),
  modernization: object().shape({
    method: mixed<ModernizationMethodEnum>(),
    refurbishmentYear: number()
      .transform(nullishToNumber)
      .optional()
      .typeError("MUST_BE_NUMBER")
      .integer("MUST_BE_INTEGER")
      .test({
        name: "refurbishment-year-range",
        test: function (value) {
          if (value === undefined || value === null) return true;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const constructionYear = (this as any).from?.[1]?.value?.buildingData?.constructionYear;

          let minYear = validationLimits.refurbishmentYear.min;
          if (
            constructionYear !== undefined &&
            constructionYear !== null &&
            Number.isInteger(constructionYear) &&
            constructionYear >= validationLimits.constructionYear.min &&
            constructionYear <= validationLimits.constructionYear.max
          ) {
            minYear = constructionYear;
          }

          if (value < minYear || value > validationLimits.refurbishmentYear.max) {
            return this.createError({
              message: `VALUE_OUT_OF_RANGE|${minYear}|${validationLimits.refurbishmentYear.max}`,
              path: this.path, // Ensure error is attributed to this field
            });
          }

          return true;
        },
      }),
    completionStatus: mixed<CompletionStatusType>().required("REQUIRED_FIELD"),
    cost: number().when("method", {
      is: (method: ModernizationMethodEnum) => method === ModernizationMethodEnum.costs,
      then: (schema) => schema.required("REQUIRED_FIELD"),
      otherwise: (schema) => schema.optional().transform((value) => (value === "" ? undefined : value)),
    }),
  }),
  financialAndRentalInfo: object().shape({
    completionStatus: mixed<CompletionStatusType>().required("REQUIRED_FIELD"),
  }),
  propertyImages: object().shape({
    completionStatus: mixed<CompletionStatusType>().required("REQUIRED_FIELD"),
  }),
});
